import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const sections = [
  {
    state: 'Kentucky',
    flag: 'KY',
    color: '#D4AF37',
    threshold: '1 Employee',
    thresholdNote: 'Any employer with even one employee must secure coverage.',
    rules: [
      { icon: 'ri-user-add-line', title: 'Who Counts as an Employee', body: 'Family members, temporary workers, and part-time workers all count toward the employee threshold. There are no exceptions for relatives or seasonal help.' },
      { icon: 'ri-map-pin-2-line', title: 'Out-of-State Employers', body: 'If your business is based outside Kentucky but you perform any work in the state, you are required to carry Kentucky workers\' compensation coverage. An "all states" endorsement alone does not satisfy this requirement — Kentucky must be specifically covered.' },
      { icon: 'ri-bank-card-line', title: 'Premium Deductions Prohibited', body: 'Kentucky law explicitly prohibits employers from deducting workers\' compensation premium costs from employee wages. The cost of coverage is entirely the employer\'s responsibility.' },
      { icon: 'ri-shield-check-line', title: 'Coverage Options', body: 'Employers may secure coverage through a licensed insurance policy or through an approved self-insurance arrangement. Both options must meet state requirements.' },
    ],
  },
  {
    state: 'Tennessee',
    flag: 'TN',
    color: '#D4AF37',
    threshold: '5 Employees (Non-Construction)',
    thresholdNote: 'Construction & coal mining: 1 employee triggers coverage.',
    rules: [
      { icon: 'ri-user-add-line', title: 'Who Counts as an Employee', body: 'Tennessee counts minors, working family members, and part-time employees when determining the 5-employee threshold. For non-construction sole proprietorships, LLCs, and partnerships, owners are not included in the count. Corporate officers working full time are included, though they may be eligible to exclude themselves from the policy.' },
      { icon: 'ri-building-2-line', title: 'Construction & Mining — Stricter Rules', body: 'Construction employers with 1 or more employees must carry coverage. This includes seasonal, part-time, family-member, and as-needed workers. Coal mining employers must also secure coverage with just 1 employee. Construction owners may exempt themselves through the state\'s Exemption Registry, but employees must still be covered.' },
      { icon: 'ri-file-list-3-line', title: 'Independent Contractor Test', body: 'Tennessee uses a 7-factor test to determine whether a worker is truly an independent contractor or an employee. A 1099 form alone does not settle the issue — the state looks at the full working relationship. Misclassification can expose employers to significant liability.' },
      { icon: 'ri-global-line', title: 'Out-of-State Employers', body: 'Out-of-state employers with employees working in Tennessee may be subject to Tennessee law. For non-construction businesses, there can be a limited exemption when the work is temporary and the employer already has workers\' comp coverage in another state.' },
    ],
  },
  {
    state: 'Virginia',
    flag: 'VA',
    color: '#D4AF37',
    threshold: 'More Than 2 Employees',
    thresholdNote: 'Coverage required once you regularly employ more than 2 workers.',
    rules: [
      { icon: 'ri-user-add-line', title: 'Who Counts as an Employee', body: 'Virginia casts a very wide net. Part-time, seasonal, temporary workers, minors, trainees, immigrants, and working family members all count. Virginia also counts subcontractor employees when determining whether the threshold is met — if those subcontractors are assisting in the same trade, business, or occupation or helping fulfill a contract.' },
      { icon: 'ri-tools-line', title: 'Contractor & Subcontractor Rules', body: 'A contractor generally must count its own employees plus subcontractor employees when determining whether coverage is required. This applies even if the subcontractor already carries its own workers\' comp policy. Carriers may also charge premium for subcontractors, so contractors should keep certificates of insurance on file for all subs to protect themselves at audit.' },
      { icon: 'ri-map-pin-2-line', title: 'Out-of-State Employers — No Reciprocity', body: 'Virginia requires Virginia coverage for any work performed in the state, even temporary work. Virginia does not recognize reciprocity with other states. Listing Virginia only under Item 3C of a workers\' comp policy is not sufficient — Virginia generally needs to be added to Item 3A to satisfy the requirement.' },
    ],
  },
];

export default function WorkersCompArticle() {
  useEffect(() => {
    document.title = 'Workers Comp Requirements KY TN VA | Summit Appalachia Insurance';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'State-by-state breakdown of workers\' compensation requirements, thresholds, and contractor rules for employers operating across Kentucky, Tennessee, and Virginia.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/resources/workers-comp-ky-tn-va`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources/workers-comp-ky-tn-va#article`,
      url: `${import.meta.env.VITE_SITE_URL}/resources/workers-comp-ky-tn-va`,
      headline: 'Workers\' Comp in KY, TN & VA: State-by-State Requirements',
      description: 'A state-by-state breakdown of workers\' compensation requirements, thresholds, and contractor rules for employers operating across Kentucky, Tennessee, and Virginia.',
      datePublished: '2026-02-01',
      dateModified: '2026-04-06',
      author: { '@type': 'Organization', name: 'Summit Appalachia Insurance' },
      publisher: {
        '@type': 'Organization',
        name: 'Summit Appalachia Insurance',
        url: import.meta.env.VITE_SITE_URL,
      },
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Workers Compensation Insurance' },
        { '@type': 'State', name: 'Kentucky' },
        { '@type': 'State', name: 'Tennessee' },
        { '@type': 'State', name: 'Virginia' },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-wc-article';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-wc-article')?.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', import.meta.env.VITE_SITE_URL ?? '/');
    };
  }, []);

  return (
    <div className="font-sans bg-[#000000]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7b63645de47d677ff480245f5668c1c7.jpg"
            alt="Workers Compensation"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            {' '}/ <Link to="/resources" className="hover:text-[#D4AF37] transition-colors">Resources</Link>
            {' '}/ Article
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Articles</span>
            <span className="text-white/30 text-xs font-sans">6 min read · Feb 2026</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Workers' Comp in<br />
            <span className="text-[#D4AF37]">KY, TN & VA</span>
          </h1>
          <p className="text-white/50 text-base font-sans mt-4 max-w-2xl leading-relaxed">
            A state-by-state breakdown of workers' compensation requirements, thresholds, and contractor rules for employers operating across the Appalachian region.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 mb-12">
            <p className="text-white/70 text-base font-sans leading-relaxed">
              All three states generally require workers' compensation coverage once an employer hits a threshold — but the threshold varies significantly by state. Kentucky requires coverage with just <strong className="text-white">1 employee</strong>, Tennessee requires coverage at <strong className="text-white">5 employees</strong> for most non-construction businesses, and Virginia requires coverage once you regularly employ <strong className="text-white">more than 2 employees</strong>. Understanding these differences is critical for any employer operating across state lines.
            </p>
          </div>

          {/* Quick Reference Table */}
          <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-md overflow-hidden mb-16">
            <div className="px-6 py-4 border-b border-[#D4AF37]/15">
              <h2 className="font-serif text-xl text-white">Quick Reference: Coverage Thresholds</h2>
            </div>
            <div className="divide-y divide-white/5">
              {[
                { state: 'Kentucky', threshold: '1 employee', note: 'Family, temp & part-time workers all count' },
                { state: 'Tennessee (Non-Construction)', threshold: '5 employees', note: 'Owners excluded; corporate officers included' },
                { state: 'Tennessee (Construction / Mining)', threshold: '1 employee', note: 'Seasonal, part-time & family workers count' },
                { state: 'Virginia', threshold: 'More than 2 employees', note: 'Subcontractor employees may count toward threshold' },
              ].map((row, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between px-6 py-4 gap-2">
                  <span className="text-white font-sans text-sm font-semibold w-full sm:w-64">{row.state}</span>
                  <span className="text-[#D4AF37] font-sans text-sm font-bold w-full sm:w-40">{row.threshold}</span>
                  <span className="text-white/40 font-sans text-xs flex-1">{row.note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* State Sections */}
      {sections.map((s, idx) => (
        <section key={idx} className={`py-16 ${idx % 2 === 0 ? 'bg-[#000000]' : 'bg-[#0a0a0a]'}`}>
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            {/* State Header */}
            <div className="flex items-center gap-5 mb-10">
              <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37] rounded-md flex-shrink-0">
                <span className="text-black font-serif font-bold text-lg">{s.flag}</span>
              </div>
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-white">{s.state}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans">Threshold:</span>
                  <span className="text-white/60 text-xs font-sans">{s.threshold}</span>
                </div>
                <p className="text-white/35 text-xs font-sans mt-0.5">{s.thresholdNote}</p>
              </div>
            </div>

            {/* Rules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {s.rules.map((rule, ri) => (
                <div key={ri} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
                      <i className={`${rule.icon} text-lg`} />
                    </div>
                    <h3 className="font-sans text-sm font-bold text-white uppercase tracking-wide">{rule.title}</h3>
                  </div>
                  <p className="text-white/50 text-sm font-sans leading-relaxed">{rule.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Out-of-State Work Highlight */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
              <i className="ri-global-line text-xl" />
            </div>
            <h2 className="font-serif text-3xl text-white">Out-of-State Work: What You Need to Know</h2>
          </div>
          <p className="text-white/50 text-sm font-sans leading-relaxed mb-8">
            For employers operating across state lines, coverage requirements can get complicated fast. Here's a summary of how each state handles out-of-state employers:
          </p>
          <div className="space-y-4">
            {[
              { state: 'Kentucky', icon: 'ri-map-pin-line', detail: 'Out-of-state employers performing any work in Kentucky must carry Kentucky workers\' comp coverage. An "all states" endorsement alone does not satisfy the requirement — Kentucky must be specifically listed on the policy.' },
              { state: 'Tennessee', icon: 'ri-map-pin-line', detail: 'Out-of-state employers with employees working in Tennessee may be subject to Tennessee law. For non-construction businesses, a limited exemption may apply when the work is temporary and the employer already has workers\' comp coverage in another state.' },
              { state: 'Virginia', icon: 'ri-map-pin-line', detail: 'Virginia requires Virginia coverage for work performed in the state, even for temporary work. Virginia does not recognize reciprocity with other states. Listing Virginia only under Item 3C is not sufficient — it generally needs to be added to Item 3A of the policy.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-5 bg-[#111111] border border-[#D4AF37]/15 rounded-md p-5">
                <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37] flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-base`} />
                </div>
                <div>
                  <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans">{item.state}</span>
                  <p className="text-white/50 text-sm font-sans leading-relaxed mt-1">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contractor / Subcontractor Callout */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-md p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-tools-line text-xl" />
              </div>
              <h2 className="font-serif text-2xl text-white">Contractor & Subcontractor Considerations</h2>
            </div>
            <p className="text-white/50 text-sm font-sans leading-relaxed mb-6">
              This is one of the most important — and most overlooked — areas for contractors working across the tri-state region.
            </p>
            <div className="space-y-5">
              <div>
                <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans">Virginia</span>
                <p className="text-white/50 text-sm font-sans leading-relaxed mt-2">
                  A contractor must generally count its own employees <em className="text-white/70">plus</em> subcontractor employees when determining whether coverage is required — even if the subcontractor already carries its own workers' comp policy. Carriers may charge premium for subcontractors, so contractors should keep certificates of insurance on file for all subs to protect themselves at audit.
                </p>
              </div>
              <div className="border-t border-white/5 pt-5">
                <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans">Tennessee</span>
                <p className="text-white/50 text-sm font-sans leading-relaxed mt-2">
                  Tennessee uses a 7-factor test to determine whether a worker is truly an independent contractor or an employee. A 1099 form alone does not settle the issue. Tennessee also has special construction-service-provider rules and an Exemption Registry for eligible owners who wish to exclude themselves from coverage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#000000] border-t border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Not Sure If You're Covered?</h2>
          <p className="text-white/40 text-sm font-sans leading-relaxed mb-8 max-w-xl mx-auto">
            Workers' comp requirements are complex and vary by state, industry, and workforce type. Our team specializes in multi-state coverage across Kentucky, Tennessee, Virginia, West Virginia, and Ohio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact"
              className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors whitespace-nowrap cursor-pointer">
              Get a Free Quote
            </Link>
            <Link to="/resources"
              className="px-8 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer">
              Back to Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
